import { useState, type FormEvent } from "react";
import { api } from "../api/client";
import { useErrorHandler } from "../auth/AuthContext";
import { formatCents, parseDollarsToCents } from "../lib/money";
import { ErrorMessage, Field, Modal } from "./common";
import { SpendConfirm } from "./SpendConfirm";

/**
 * The child's spend flow: enter an amount, then confirm it against an animated
 * picture of what it costs them.
 *
 * The confirmation is a separate step rather than an inline warning because the
 * point is to make a child pause and look at the proportion before the money
 * goes.
 */
export function SpendMoney({
  childId,
  spendBalanceCents,
  onClose,
  onSpent,
}: {
  childId: string;
  spendBalanceCents: number;
  onClose: () => void;
  onSpent: () => Promise<void> | void;
}) {
  const toMessage = useErrorHandler();

  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  function review(e: FormEvent) {
    e.preventDefault();
    setError("");

    const cents = parseDollarsToCents(amount);
    if (cents === null || cents <= 0) {
      setError("Enter how much you want to spend, like 3.50");
      return;
    }
    // Checked here for a fast, friendly message; the server enforces it too,
    // and the server's answer is the one that counts.
    if (cents > spendBalanceCents) {
      setError(
        `That's more than your Spend money. You have ${formatCents(spendBalanceCents)}.`,
      );
      return;
    }
    setConfirming(cents);
  }

  async function confirm() {
    if (confirming === null) return;
    setBusy(true);
    setError("");
    try {
      await api.spend(childId, { amountCents: confirming, note });
      await onSpent();
      onClose();
    } catch (err) {
      setError(toMessage(err));
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  }

  if (confirming !== null) {
    return (
      <SpendConfirm
        amountCents={confirming}
        spendBalanceCents={spendBalanceCents}
        note={note}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          setConfirming(null);
          setError("");
        }}
      />
    );
  }

  return (
    <Modal title="Spend some money" onClose={onClose}>
      <form className="stack" onSubmit={review}>
        <ErrorMessage>{error}</ErrorMessage>

        <p className="muted">
          You have {formatCents(spendBalanceCents)} to spend.
        </p>

        <Field label="How much?" hint="For example 3 or 3.50">
          <input
            className="input"
            inputMode="decimal"
            autoFocus
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <Field label="What for?">
          <input
            className="input"
            placeholder="Comic book"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <div className="row row--between">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary">
            Next
          </button>
        </div>
      </form>
    </Modal>
  );
}
